import { ApolloLink, Observable } from '@apollo/client';
import {
  cancelSpan,
  endSpan,
  findActiveSpan,
  startSpan,
} from '@/analytics/fennel/performance/default-tracker';
import { PerformanceEventName } from '@/analytics/fennel/performance/types';

export interface ApolloPerformanceContext {
  parentSpanName: PerformanceEventName;
  spanName: PerformanceEventName;
}

export const apolloPerformanceLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  const performanceContext = context.performance as
    | ApolloPerformanceContext
    | undefined;

  if (!performanceContext) {
    return forward(operation);
  }

  const parentSpan = findActiveSpan(performanceContext.parentSpanName);
  if (!parentSpan) {
    return forward(operation);
  }

  const childSpan = startSpan(performanceContext.spanName, {
    parentSpanId: parentSpan.spanId,
  });

  if (!childSpan) {
    return forward(operation);
  }

  // x-request-id lets failing requests be looked up in CloudWatch.
  operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, 'x-request-id': childSpan.traceId },
  }));

  let finalized = false;

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      complete: () => {
        if (!finalized) {
          finalized = true;
          endSpan(childSpan);
        }
        observer.complete();
      },
      error: (error) => {
        if (!finalized) {
          finalized = true;
          cancelSpan(childSpan);
        }
        observer.error(error);
      },
      // End the span on the first result rather than on complete. For standard
      // single-result queries these fire nearly simultaneously. This measures
      // time-to-first-data, which is the relevant metric for perceived load.
      next: (result) => {
        if (!finalized) {
          finalized = true;
          endSpan(childSpan);
        }
        observer.next(result);
      },
    });

    return () => {
      if (!finalized) {
        finalized = true;
        cancelSpan(childSpan);
      }
      subscription.unsubscribe();
    };
  });
});
